import { FC, MouseEvent, PropsWithChildren } from "react";

import { Box } from "@mui/material";
import { AvailablePaymentCurrency } from "@simplyk/common";

import { splitLabelLastWord } from "./helpers/splitLabelLastWord";

import { useDynamicThemeAwareVariables } from "@/common/hooks/useDynamicThemeAwareVariables";
import { Trans, useTranslate } from "@/common/hooks/useTranslate";
import { FormCheckbox } from "@/forms-design-system/FormCheckbox";
import { FormPaymentOrderItem } from "@/forms-design-system/FormPaymentSummary/FormPaymentOrderItem";
import { FormInfoTooltip } from "@/forms-design-system/FormTooltip";
import { FormTypography, FormTypographyProps } from "@/forms-design-system/FormTypography";

export interface RepeatDonorTipCheckboxLineProps {
  checked: boolean;
  onToggle: (checked: boolean) => void;
  tipAmount: number;
  tipPercentage: number;
  organizationName: string;
  currency: AvailablePaymentCurrency;
  isOrganizationVoice?: boolean;
  formattedAmountWithoutTip?: string;
}

export const RepeatDonorTipCheckboxLine: FC<RepeatDonorTipCheckboxLineProps> = ({
  checked,
  onToggle,
  tipAmount,
  tipPercentage,
  organizationName,
  currency,
  isOrganizationVoice = false,
  formattedAmountWithoutTip,
}) => {
  const { t } = useTranslate();

  const { subtleVibe, tooltipColor } = useDynamicThemeAwareVariables(({ formsPalette, mode }) => ({
    subtleVibe: (mode === "light" ? "quiet" : "supershy") as FormTypographyProps["vibe"],
    tooltipColor: mode === "light" ? formsPalette.text.quiet : formsPalette.text.supershy,
  }));

  const handleRowClick = (event: MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest("label, input")) {
      return;
    }
    onToggle(!checked);
  };

  const tooltipTitle = isOrganizationVoice
    ? t("common", "paymentSummary.repeatDonorTip.orgVoiceTooltip", { amount: formattedAmountWithoutTip })
    : t("common", "paymentSummary.repeatDonorTip.tooltip", { organizationName });

  const tooltip = (
    <FormInfoTooltip
      size="small"
      sx={{ marginInlineStart: 0.5, verticalAlign: "middle", color: tooltipColor }}
      title={tooltipTitle}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    />
  );

  const SubtleWithTooltip: FC<PropsWithChildren> = ({ children }) => (
    <FormTypography variant="body2" vibe={subtleVibe} component="span" sx={{ whiteSpace: "nowrap" }}>
      {children}
      {tooltip}
    </FormTypography>
  );

  const renderOrganizationVoiceLabel = () => {
    const { labelStart, labelLastWord } = splitLabelLastWord(
      t("common", "paymentSummary.repeatDonorTip.orgVoiceLabel")
    );

    return (
      <>
        {labelStart}
        <Box component="span" sx={{ whiteSpace: "nowrap" }}>
          {labelLastWord}
          {tooltip}
        </Box>
      </>
    );
  };

  return (
    <FormPaymentOrderItem
      onClick={handleRowClick}
      sx={{ cursor: "pointer" }}
      label={
        <FormCheckbox
          name="repeatDonorTip"
          size="small"
          checked={checked}
          onChange={(_event, newChecked) => onToggle(newChecked)}
          label={
            isOrganizationVoice ? (
              renderOrganizationVoiceLabel()
            ) : (
              <Trans
                space="common"
                i18nKey="paymentSummary.repeatDonorTip.checkboxLabel"
                values={{ percentage: tipPercentage, organizationName }}
                components={{
                  subtle: <SubtleWithTooltip />,
                }}
              />
            )
          }
          data-test="repeat-donor-tip-checkbox"
        />
      }
      price={tipAmount}
      currency={currency}
      data-test="repeat-donor-tip-line"
    />
  );
};
